import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "../ui/card.tsx";

import { Button } from "../ui/button.tsx";
import { Input } from "../ui/input.tsx";
import { Badge } from "../ui/badge.tsx";

import { Settings, Trash2 } from "lucide-react";

interface AdminMember {
  id: number;
  Email: string;
  FirstName: string;
  LastName: string;
  GraduationYear: number;
  is_staff: boolean;
  AssociationRole?: string;
}

interface MembersTabProps {
  members: AdminMember[];
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  openMemberModal: (member: AdminMember) => void;

  // ✅ Üye Silme Fonksiyonu
  handleDeleteMember: (memberId: number) => Promise<void>;
}

export function MembersTab({
  members,
  searchTerm,
  setSearchTerm,
  openMemberModal,
  handleDeleteMember
}: MembersTabProps) {
  // ✅ Arama filtresi
  const filteredMembers = members.filter((m) => {
    const term = searchTerm.toLowerCase();

    return (
      (m.FirstName || "").toLowerCase().includes(term) ||
      (m.LastName || "").toLowerCase().includes(term) ||
      (m.Email || "").toLowerCase().includes(term)
    );
  });

  // ✅ Silme işlemi
  const onDeleteClick = async (member: AdminMember) => {
    const confirmDelete = window.confirm(
      `${member.FirstName} ${member.LastName} adlı üyeyi silmek istediğine emin misin?`
    );

    if (!confirmDelete) return;

    await handleDeleteMember(member.id);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Üye Yönetimi</CardTitle>
        <CardDescription>
          Üyeleri düzenleyin, rol atayın veya admin yetkisi verin.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {/* ✅ Arama kutusu */}
        <Input
          placeholder="Ara..."
          className="mb-4"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {/* ✅ Üye listesi */}
        <div className="max-h-[500px] overflow-y-auto space-y-2">
          {filteredMembers.map((m) => (
            <div
              key={m.id}
              className="flex justify-between p-3 border rounded items-center hover:bg-slate-50"
            >
              {/* ✅ Üye bilgileri */}
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold">
                  {(m.FirstName?.[0] || "?")}
                  {(m.LastName?.[0] || "?")}
                </div>

                <div>
                  {/* İsim + Badge */}
                  <div className="font-bold flex items-center gap-2">
                    {m.FirstName} {m.LastName}

                    {/* Admin etiketi */}
                    {m.is_staff && (
                      <Badge className="text-xs bg-red-100 text-red-800">
                        Admin
                      </Badge>
                    )}

                    {/* Rol etiketi */}
                    {m.AssociationRole?.trim() && (
                      <Badge className="text-xs bg-blue-100 text-blue-800">
                        {m.AssociationRole}
                      </Badge>
                    )}
                  </div>

                  {/* Email */}
                  <div className="text-xs text-slate-500">{m.Email}</div>
                </div>
              </div>

              {/* ✅ Butonlar */}
              <div className="flex gap-2">
                {/* Yönet */}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => openMemberModal(m)}
                >
                  <Settings className="h-4 w-4" />
                  Yönet
                </Button>

                {/* Sil */}
                <Button
                  variant="ghost"
                  size="sm"
                  title="Üyeyi Sil"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onDeleteClick(m)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          {/* Liste boşsa */}
          {filteredMembers.length === 0 && (
            <div className="text-center text-slate-400 py-6">
              Üye bulunamadı.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
